import { Database, X, HardDrive, CalendarClock, Cloud, History, Download, Mail, CloudUpload, Settings, ShieldAlert } from "lucide-react";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { PasswordInput } from "../shared/PasswordInput";
import { TelcavoipCloudExtras } from "./TelcavoipCloudExtras";

/**
 * Backup / Schedule / Cloud / History panel for the Admin Dashboard.
 * Extracted from `AdminDashboard.js` (refactor 22 Feb 2026 — pure code move).
 *
 * All state and side-effects remain in the parent — this component is a
 * pure presentational shell receiving handlers via props.
 */
export const AdminBackupPanel = ({
  show,
  onClose,
  backupTab,
  setBackupTab,
  backupStatus,
  backupSchedule,
  backupHistory,
  cloudConfig,
  setCloudConfig,
  stats,
  backupDownloading,
  backupEmailing,
  savingCloud,
  uploadingCloud,
  downloadBackup,
  sendBackupEmail,
  toggleAutoBackup,
  updateBackupInterval,
  saveCloudConfig,
  uploadToCloud,
  // TelcaVoIP Cloud (pCloud-backed) — token managed server-side,
  // LoJack admins only see a single ON/OFF toggle.
  telcavoipCloudStatus,
  telcavoipCloudToggling,
  telcavoipCloudRunning,
  toggleTelcavoipCloud,
  runTelcavoipCloudNow,
}) => {
  if (!show) return null;

  return (
    <div className="wallet-card mb-6 animate-fade-in" data-testid="backup-panel">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-lg font-semibold text-white flex items-center gap-2"><Database className="text-lojack-red" size={20} />Gestione Backup Database</h3>
        <div className="flex items-center gap-2">
          {/* Panic button: jumps to the Disaster Recovery wizard. Always
              visible regardless of which tab is open so the operator
              never has to hunt for it during an emergency. */}
          <Link
            to="/admin/disaster-recovery"
            className="hidden sm:inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-950/40 border border-red-500/40 hover:bg-red-950/60 text-red-300 rounded-lg text-xs font-semibold transition"
            data-testid="dr-launch-btn"
            title="Wizard di ripristino emergenza dal backup pCloud"
          >
            <ShieldAlert size={14} /> Disaster Recovery
          </Link>
          <button onClick={onClose} className="text-gray-500 hover:text-white"><X size={18} /></button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-5 border-b border-gray-800 pb-3">
        {[
          { key: 'main', label: 'Backup', icon: HardDrive },
          { key: 'schedule', label: 'Programmazione', icon: CalendarClock },
          { key: 'cloud', label: 'Cloud Storage', icon: Cloud },
          { key: 'history', label: 'Storico', icon: History },
        ].map(t => (
          <button key={t.key} onClick={() => setBackupTab(t.key)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${backupTab === t.key ? 'bg-lojack-red text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800'}`}
            data-testid={`backup-tab-${t.key}`}>
            <t.icon size={14} />{t.label}
          </button>
        ))}
      </div>

      {/* Main Tab */}
      {backupTab === 'main' && (
        <div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
            <div className="bg-gray-800/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Ultimo Backup</p>
              <p className="text-white font-semibold">{backupStatus?.last_backup ? new Date(backupStatus.last_backup).toLocaleString('it-IT') : 'Mai eseguito'}</p>
              {backupStatus?.days_ago !== null && (
                <p className={`text-xs mt-1 ${backupStatus.days_ago >= 7 ? 'text-lojack-clause-500' : 'text-lojack-blue-500'}`}>
                  {backupStatus.days_ago === 0 ? 'Oggi' : `${backupStatus.days_ago} giorni fa`}
                </p>
              )}
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Dati nel Database</p>
              <p className="text-white font-semibold">{stats?.total_customers || 0} clienti</p>
              <p className="text-gray-400 text-xs mt-1">{stats?.contracts_signed || 0} contratti firmati</p>
            </div>
            <div className="bg-gray-800/50 rounded-xl p-4">
              <p className="text-gray-500 text-xs mb-1">Backup Totali Eseguiti</p>
              <p className="text-white font-semibold">{backupHistory.length}</p>
              <p className="text-gray-400 text-xs mt-1">
                {backupHistory.filter(b => b.type === 'manual').length} manuali, {backupHistory.filter(b => b.type === 'email').length} email, {backupHistory.filter(b => b.type === 'cloud').length} cloud
              </p>
            </div>
          </div>
          <div className="flex gap-3 flex-wrap">
            <button onClick={downloadBackup} disabled={backupDownloading} className="btn-lojack flex items-center gap-2" data-testid="download-backup-btn">
              {backupDownloading ? <div className="spinner-sm"></div> : <Download size={18} />}
              {backupDownloading ? 'Download...' : 'Scarica Backup'}
            </button>
            <button onClick={sendBackupEmail} disabled={backupEmailing} className="btn-lojack-outline flex items-center gap-2" data-testid="email-backup-btn">
              {backupEmailing ? <div className="spinner-sm"></div> : <Mail size={18} />}
              {backupEmailing ? 'Invio...' : 'Invia via Email'}
            </button>
            {cloudConfig.enabled && (
              <button onClick={uploadToCloud} disabled={uploadingCloud} className="flex items-center gap-2 px-4 py-2 bg-blue-600/10 border border-blue-600/30 rounded-lg text-blue-400 text-sm font-medium hover:bg-blue-600/20 transition-colors" data-testid="upload-cloud-btn">
                {uploadingCloud ? <div className="spinner-sm"></div> : <CloudUpload size={18} />}
                {uploadingCloud ? 'Upload...' : 'Carica su Cloud'}
              </button>
            )}
          </div>
        </div>
      )}

      {/* Schedule Tab */}
      {backupTab === 'schedule' && (
        <div>
          <div className="bg-gray-800/50 rounded-xl p-5 mb-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-medium">Backup Automatico</p>
                <p className="text-gray-500 text-xs mt-1">I backup programmati vengono inviati automaticamente via email agli amministratori</p>
              </div>
              <button onClick={toggleAutoBackup} className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${backupSchedule?.enabled ? 'bg-lojack-blue-600' : 'bg-gray-700'}`} data-testid="auto-backup-toggle">
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${backupSchedule?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {backupSchedule?.enabled && (
              <div>
                <p className="text-gray-400 text-xs mb-3">Frequenza:</p>
                <div className="grid grid-cols-4 gap-2">
                  {[
                    { label: 'Giornaliero', days: 1, desc: 'Ogni giorno' },
                    { label: 'Settimanale', days: 7, desc: 'Ogni 7 giorni' },
                    { label: 'Quindicinale', days: 15, desc: 'Ogni 15 giorni' },
                    { label: 'Mensile', days: 30, desc: 'Ogni 30 giorni' }
                  ].map(opt => (
                    <button key={opt.days} onClick={() => updateBackupInterval(opt.days)}
                      className={`p-3 rounded-xl text-center transition-colors border ${backupSchedule?.interval_days === opt.days ? 'bg-lojack-red/10 border-lojack-red/30 text-white' : 'bg-gray-800/50 border-gray-700 text-gray-400 hover:border-gray-600'}`}
                      data-testid={`backup-interval-${opt.days}`}>
                      <p className="text-sm font-medium">{opt.label}</p>
                      <p className="text-[10px] text-gray-500 mt-1">{opt.desc}</p>
                    </button>
                  ))}
                </div>
                <p className="text-lojack-blue-500 text-xs mt-3 flex items-center gap-1"><CalendarClock size={12} />Email automatica agli admin con file JSON allegato</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Cloud Tab */}
      {backupTab === 'cloud' && (
        <div className="space-y-4">
          {/* TelcaVoIP Cloud — managed-service option. LoJack admins
              see ONLY a toggle + last-upload status; the underlying
              pCloud access token is held server-side. */}
          <div className="bg-gradient-to-br from-lojack-red/10 to-transparent border border-lojack-red/30 rounded-xl p-5" data-testid="telcavoip-cloud-card">
            <div className="flex items-center justify-between mb-3">
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold flex items-center gap-2">
                  <Cloud size={18} className="text-lojack-red flex-shrink-0" />
                  TelcaVoIP Cloud
                  <span className="px-2 py-0.5 bg-lojack-red/15 border border-lojack-red/40 rounded-full text-[10px] uppercase tracking-wider text-lojack-red font-semibold">Consigliato</span>
                </p>
                <p className="text-gray-400 text-xs mt-1">Backup automatico crittografato su infrastruttura cloud gestita da TelcaVoIP. Nessuna configurazione richiesta.</p>
              </div>
              <button
                onClick={() => toggleTelcavoipCloud && toggleTelcavoipCloud(!telcavoipCloudStatus?.enabled)}
                disabled={telcavoipCloudToggling || (!telcavoipCloudStatus?.configured && !telcavoipCloudStatus?.enabled)}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ml-3 ${telcavoipCloudStatus?.enabled ? 'bg-lojack-red' : 'bg-gray-700'} ${(telcavoipCloudToggling || (!telcavoipCloudStatus?.configured && !telcavoipCloudStatus?.enabled)) ? 'opacity-50 cursor-not-allowed' : ''}`}
                data-testid="telcavoip-cloud-toggle"
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${telcavoipCloudStatus?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>

            {!telcavoipCloudStatus?.configured && (
              <div className="bg-lojack-clause-950/30 border border-lojack-clause-500/30 rounded-lg p-3 text-xs text-lojack-clause-300 mb-2">
                ⚠ Servizio non ancora attivato lato TelcaVoIP. Contatta il supporto per abilitarlo.
              </div>
            )}

            {telcavoipCloudStatus?.enabled && (
              <div className="bg-[#0A0A0A] rounded-lg p-3 mt-3 space-y-2">
                {telcavoipCloudStatus?.last_upload_at ? (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Ultimo upload</span>
                    <span className="text-white">
                      {new Date(telcavoipCloudStatus.last_upload_at).toLocaleString('it-IT')}
                      {telcavoipCloudStatus.last_upload_size_kb && (
                        <span className="text-gray-500 ml-2">({telcavoipCloudStatus.last_upload_size_kb} KB)</span>
                      )}
                    </span>
                  </div>
                ) : (
                  <p className="text-xs text-gray-500">Nessun backup ancora caricato — usa il pulsante qui sotto per il primo test.</p>
                )}
                {telcavoipCloudStatus?.quota_bytes && (
                  <div className="flex items-center justify-between text-xs">
                    <span className="text-gray-400">Spazio cloud usato</span>
                    <span className="text-white">
                      {(((telcavoipCloudStatus.used_bytes || 0) / (1024 ** 3))).toFixed(2)} GB
                      <span className="text-gray-500"> / {((telcavoipCloudStatus.quota_bytes / (1024 ** 3))).toFixed(0)} GB</span>
                    </span>
                  </div>
                )}
                {telcavoipCloudStatus?.last_error && (
                  <div className="text-xs text-red-400 mt-2 bg-red-950/30 border border-red-500/30 rounded p-2">
                    Errore ultimo upload: {telcavoipCloudStatus.last_error}
                  </div>
                )}
                <button
                  onClick={runTelcavoipCloudNow}
                  disabled={telcavoipCloudRunning}
                  className="btn-lojack-outline flex items-center gap-2 mt-3 text-sm"
                  data-testid="telcavoip-cloud-run-now"
                >
                  {telcavoipCloudRunning ? <div className="spinner-sm"></div> : <CloudUpload size={14} />}
                  {telcavoipCloudRunning ? 'Backup in corso...' : 'Esegui backup ora'}
                </button>
              </div>
            )}

            {/* Extras: token age warning, 12-month chart, recent backups list, rotate-token modal
                (Fabrizio CR 28/02/2026 — backlog completion). Local state, no
                additional props needed from the parent.  */}
            <TelcavoipCloudExtras status={telcavoipCloudStatus} />
          </div>

          {/* Cloud Storage Esterno (provider configurabile) */}
          <div className="bg-gray-800/50 rounded-xl p-5">
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-white font-medium flex items-center gap-2"><Cloud size={16} className="text-blue-400" /> Cloud Storage Esterno</p>
                <p className="text-gray-500 text-xs mt-1">Configura uno spazio di archiviazione cloud (AWS S3, Google Cloud Storage, Azure Blob, MinIO, ecc.)</p>
              </div>
              <button onClick={() => setCloudConfig(prev => ({...prev, enabled: !prev.enabled}))}
                className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors flex-shrink-0 ${cloudConfig.enabled ? 'bg-lojack-blue-600' : 'bg-gray-700'}`} data-testid="cloud-toggle">
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${cloudConfig.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
              </button>
            </div>
            {cloudConfig.enabled && (
              <div className="space-y-3 mt-4">
                <div>
                  <label className="text-gray-400 text-xs">Provider</label>
                  <select value={cloudConfig.provider} onChange={e => setCloudConfig(prev => ({...prev, provider: e.target.value}))}
                    className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="cloud-provider">
                    <option value="aws_s3">Amazon S3</option>
                    <option value="google_cloud">Google Cloud Storage</option>
                    <option value="azure_blob">Azure Blob Storage</option>
                    <option value="minio">MinIO / S3 Compatibile</option>
                    <option value="other">Altro (S3 Compatible)</option>
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 text-xs">Endpoint URL (opzionale per AWS)</label>
                    <input value={cloudConfig.endpoint_url} onChange={e => setCloudConfig(prev => ({...prev, endpoint_url: e.target.value}))}
                      placeholder="https://s3.eu-west-1.amazonaws.com" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="cloud-endpoint" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs">Bucket / Container Name *</label>
                    <input value={cloudConfig.bucket_name} onChange={e => setCloudConfig(prev => ({...prev, bucket_name: e.target.value}))}
                      placeholder="lojack-backups" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="cloud-bucket" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-400 text-xs">Access Key *</label>
                    <input value={cloudConfig.access_key} onChange={e => setCloudConfig(prev => ({...prev, access_key: e.target.value}))}
                      placeholder="AKIA..." className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono" data-testid="cloud-access-key" />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs">Secret Key *</label>
                    <PasswordInput value={cloudConfig.secret_key} onChange={e => setCloudConfig(prev => ({...prev, secret_key: e.target.value}))}
                      placeholder="••••••••" className="w-full mt-1 px-4 py-2.5 pr-12 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm font-mono" data-testid="cloud-secret-key" />
                  </div>
                </div>
                <div>
                  <label className="text-gray-400 text-xs">Region</label>
                  <input value={cloudConfig.region} onChange={e => setCloudConfig(prev => ({...prev, region: e.target.value}))}
                    placeholder="eu-west-1" className="w-full mt-1 px-4 py-2.5 bg-[#111] border border-gray-700 rounded-xl text-white focus:border-blue-500 focus:outline-none text-sm" data-testid="cloud-region" />
                </div>
                <div className="flex gap-3 pt-2">
                  <button onClick={saveCloudConfig} disabled={savingCloud}
                    className="btn-lojack flex items-center gap-2" data-testid="save-cloud-btn">
                    {savingCloud ? <div className="spinner-sm"></div> : <Settings size={16} />}
                    {savingCloud ? 'Salvataggio...' : 'Salva Configurazione'}
                  </button>
                  <button onClick={uploadToCloud} disabled={uploadingCloud || !cloudConfig.bucket_name}
                    className="btn-lojack-outline flex items-center gap-2" data-testid="test-cloud-btn">
                    {uploadingCloud ? <div className="spinner-sm"></div> : <CloudUpload size={16} />}
                    {uploadingCloud ? 'Upload...' : 'Test Upload'}
                  </button>
                </div>
              </div>
            )}
            {!cloudConfig.enabled && (
              <p className="text-gray-600 text-xs mt-2">Attiva per configurare il salvataggio automatico dei backup su cloud esterno</p>
            )}
          </div>
        </div>
      )}

      {/* History Tab */}
      {backupTab === 'history' && (
        <div>
          {backupHistory.length > 0 ? (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {backupHistory.map((b, i) => {
                const typeIcons = { manual: Download, email: Mail, cloud: Cloud };
                const typeLabels = { manual: 'Download', email: 'Email', cloud: 'Cloud' };
                const typeColors = { manual: 'text-blue-400 bg-blue-500/10', email: 'text-lojack-blue-400 bg-lojack-blue-500/10', cloud: 'text-purple-400 bg-purple-500/10' };
                const Icon = typeIcons[b.type] || Database;
                return (
                  <div key={i} className="flex items-center justify-between bg-gray-800/50 rounded-xl px-4 py-3" data-testid={`backup-history-${i}`}>
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${typeColors[b.type] || 'text-gray-400 bg-gray-700'}`}>
                        <Icon size={14} />
                      </div>
                      <div>
                        <p className="text-white text-sm font-medium">{typeLabels[b.type] || b.type}</p>
                        <p className="text-gray-500 text-xs">{new Date(b.created_at).toLocaleString('it-IT')}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      {b.size_kb && <p className="text-gray-400 text-xs">{b.size_kb} KB</p>}
                      {b.stats && <p className="text-gray-600 text-[10px]">{b.stats.customers_count} clienti, {b.stats.signed_contracts_count} contratti</p>}
                      {b.email && <p className="text-gray-600 text-[10px]">{b.email}</p>}
                      {b.bucket && <p className="text-gray-600 text-[10px]">{b.bucket}/{b.filename}</p>}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-8">
              <History size={32} className="mx-auto mb-3 text-gray-700" />
              <p className="text-gray-500 text-sm">Nessun backup eseguito</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AdminBackupPanel;

AdminBackupPanel.propTypes = {
  show: PropTypes.bool,                        // panel only renders when truthy
  onClose: PropTypes.func.isRequired,
  backupTab: PropTypes.string,                 // 'main'|'schedule'|'cloud'|'history'
  setBackupTab: PropTypes.func.isRequired,
  backupStatus: PropTypes.object,
  backupSchedule: PropTypes.object,
  backupHistory: PropTypes.array,
  cloudConfig: PropTypes.object,
  setCloudConfig: PropTypes.func.isRequired,
  stats: PropTypes.object,
  backupDownloading: PropTypes.bool,
  backupEmailing: PropTypes.bool,
  savingCloud: PropTypes.bool,
  uploadingCloud: PropTypes.bool,
  downloadBackup: PropTypes.func.isRequired,
  sendBackupEmail: PropTypes.func.isRequired,
  toggleAutoBackup: PropTypes.func.isRequired,
  updateBackupInterval: PropTypes.func.isRequired,
  saveCloudConfig: PropTypes.func.isRequired,
  uploadToCloud: PropTypes.func.isRequired,
  // TelcaVoIP Cloud — managed-service option (pCloud-backed).
  telcavoipCloudStatus: PropTypes.object,
  telcavoipCloudToggling: PropTypes.bool,
  telcavoipCloudRunning: PropTypes.bool,
  toggleTelcavoipCloud: PropTypes.func,
  runTelcavoipCloudNow: PropTypes.func,
};
